import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeftRight,
  ArrowUpDown,
  ChevronDown,
  Clock,
  FlaskConical,
  Gift,
  Package,
  RefreshCw,
  RotateCcw,
  Search,
  TrendingUp,
  X,
} from "lucide-react";
import { useState, useMemo, useCallback, memo, useRef, useEffect } from "react";
import {
  useRefreshSuperAdminShops,
  useSuperAdminShops,
  useSuperAdminShopsCatalog,
  useUpdateSuperAdminShopMeta,
} from "@/api/super-admin/use-super-admin-shops";
import {
  useProbeAllCatalogCounts,
  useProbeCatalogShopCount,
} from "@/api/super-admin/use-super-admin-catalog-sync";
import { useSyncCancelledReturnRequestsStats } from "@/api/super-admin/use-super-admin-statistics";
import {
  useSetSuperAdminShopsClient,
  useSuperAdminClients,
} from "@babackapp/private-api-client/client/use-super-admin-clients";
import { AdminPageShell } from "./admin-tab-pages";
import {
  CLIENT_STATUS_LABELS,
  type ClientStatus,
  formatDate,
  formatRelative,
  INTEGRATIONS,
  StatPanel,
} from "./shop-shared";

// ─── Types ────────────────────────────────────────────────────────────────────

type SortKey =
  | "name"
  | "totalOrders"
  | "ordersLast30Days"
  | "ordersLast7Days"
  | "ordersLast24Hours"
  | "totalReturnRequests"
  | "returnRequestsLast30Days"
  | "returnRequestsLast7Days"
  | "returnRequestsLast24Hours"
  | "lastActivityAt"
  | "membersCount"
  | "catalogProductCount";
type SortDir = "asc" | "desc";

interface ShopRowData {
  id: string;
  name: string;
  slug: string;
  planCode: string | null;
  isTestShop: boolean | null;
  clientStatus: string | null;
  isIncludedInStats: boolean | null;
  clientAccountId: string | null;
  clientName: string | null;
  csm: string | null;
  totalOrders: number;
  ordersLast30Days: number;
  ordersLast7Days: number;
  ordersLast24Hours: number;
  totalReturnRequests: number;
  returnRequestsLast30Days: number;
  returnRequestsLast7Days: number;
  returnRequestsLast24Hours: number;
  lastActivityAt: number | null;
  // Feature flags
  hasExchanges: boolean;
  hasGiftCode: boolean;
  hasWorkflowLogistician: boolean;
  activeIntegrations: string[];
  catalogProductCount: number | null;
  catalogProductCountProbedAt: number | null;
  isCatalogProductCountApproximate: boolean | null;
  catalogArchivedProductCount: number | null;
  isCatalogArchivedProductCountApproximate: boolean | null;
  loadedCatalogProductCount: number | null;
  loadedCatalogArchivedProductCount: number | null;
  planProductRecommendation: boolean | null;
  isActive: boolean | null;
}

type FeatureKey =
  | "exchanges"
  | "giftCode"
  | "logistician"
  | keyof typeof INTEGRATIONS;

// ─── Sub-components ───────────────────────────────────────────────────────────

function ActivityBadge({ last7Days }: { last7Days: number }) {
  if (last7Days >= 50)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
        <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
        Actif
      </span>
    );
  if (last7Days >= 5)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
        <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
        Modéré
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      Faible
    </span>
  );
}

// ─── Feature KPI ─────────────────────────────────────────────────────────────

const FEATURE_COLORS = {
  blue: {
    bar: "bg-blue-400",
    text: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-100",
  },
  violet: {
    bar: "bg-violet-400",
    text: "text-violet-600",
    bg: "bg-violet-50",
    border: "border-violet-100",
  },
  orange: {
    bar: "bg-orange-400",
    text: "text-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-100",
  },
};

function FeatureKpi({
  icon,
  label,
  count,
  total,
  pct,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  total: number;
  pct: number;
  color: keyof typeof FEATURE_COLORS;
}) {
  const c = FEATURE_COLORS[color];
  return (
    <div className={`rounded-lg border ${c.border} ${c.bg} p-4 space-y-2`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-xs font-medium text-muted-foreground">
            {label}
          </span>
        </div>
        <span className={`text-xs font-semibold tabular-nums ${c.text}`}>
          {pct}%
        </span>
      </div>
      <div className="flex items-end justify-between gap-2">
        <span className="text-2xl font-bold tabular-nums">{count}</span>
        <span className="text-xs text-muted-foreground mb-0.5">
          / {total} shops
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-black/10 overflow-hidden">
        <div
          className={`h-full rounded-full ${c.bar} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─── Filter Dropdown ──────────────────────────────────────────────────────────

function FilterDropdown({
  label,
  activeCount,
  children,
  onClear,
}: {
  label: string;
  activeCount?: number;
  children: React.ReactNode;
  onClear?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={[
          "flex items-center gap-1.5 rounded-md border bg-background px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted/60",
          open ? "border-violet-400 ring-1 ring-violet-200" : "",
          activeCount ? "border-violet-300" : "",
        ].join(" ")}
      >
        <span className="text-xs">{label}</span>
        {activeCount ? (
          <span className="flex items-center gap-1">
            <span className="rounded-full bg-violet-600 px-1.5 py-px text-[11px] font-semibold text-white leading-none">
              {activeCount}
            </span>
            {onClear && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    onClear?.();
                  }
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-2.5 w-2.5" />
              </span>
            )}
          </span>
        ) : (
          <ChevronDown
            className={`h-3 w-3 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1.5 z-50 min-w-[180px] rounded-lg border bg-background shadow-md py-1.5 animate-in fade-in-0 zoom-in-95">
          {children}
        </div>
      )}
    </div>
  );
}

function DropdownCheckItem({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: () => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer hover:bg-muted/60 transition-colors select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-3.5 w-3.5 accent-violet-600 cursor-pointer"
      />
      {children}
    </label>
  );
}

function DropdownRadioItem({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: () => void;
  children: React.ReactNode;
}) {
  return (
    <label
      className={[
        "flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer hover:bg-muted/60 transition-colors select-none",
        checked ? "text-violet-700 font-medium" : "",
      ].join(" ")}
    >
      <span
        className={[
          "h-3.5 w-3.5 rounded-full border-2 flex items-center justify-center shrink-0",
          checked
            ? "border-violet-600 bg-violet-600"
            : "border-muted-foreground/40",
        ].join(" ")}
      >
        {checked && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
      </span>
      <span onClick={onChange}>{children}</span>
    </label>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type TypeFilter = "prod" | "test" | "all";
type StatsFilter = "all" | "in" | "out";
type ActivityFilter = "all" | "active" | "inactive";

const SORT_DEFAULT: SortKey = "returnRequestsLast30Days";
const DIR_DEFAULT: SortDir = "desc";
const TYPE_DEFAULT: TypeFilter = "prod";
const STATS_DEFAULT: StatsFilter = "all";
const ACTIVITY_DEFAULT: ActivityFilter = "all";

function parseStatusFilter(raw: string | null): Set<ClientStatus> {
  if (!raw) return new Set();
  return new Set(raw.split(",").filter(Boolean) as ClientStatus[]);
}

function serializeStatusFilter(set: Set<ClientStatus>): string {
  return [...set].join(",");
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data,
    isPending,
    isError,
    refetch: refetchShops,
  } = useSuperAdminShops();
  const {
    data: catalogData,
    isPending: isCatalogPending,
    refetch: refetchCatalog,
  } = useSuperAdminShopsCatalog();
  const refetch = useCallback(() => {
    void refetchShops();
    void refetchCatalog();
  }, [refetchShops, refetchCatalog]);
  const refreshMutation = useRefreshSuperAdminShops();
  const probeCatalogMutation = useProbeAllCatalogCounts();
  const probeShopMutation = useProbeCatalogShopCount();
  const syncCancelledMutation = useSyncCancelledReturnRequestsStats();

  const [probingShopIds, setProbingShopIds] = useState<Set<string>>(new Set());
  const probeSnapshotRef = useRef<Map<string, number | null>>(new Map());
  const probeStartedAtRef = useRef<number | null>(null);
  const probeInitialCountRef = useRef<number>(0);

  const catalogByShopId = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<typeof catalogData>["catalogs"][number]
    >();
    for (const c of catalogData?.catalogs ?? []) {
      map.set(c.shopId, c);
    }
    return map;
  }, [catalogData]);

  const handleProbeShopCount = useCallback(
    (shopId: string) => {
      const cat = catalogByShopId.get(shopId);
      probeSnapshotRef.current.set(
        shopId,
        cat?.catalogProductCountProbedAt ?? null,
      );
      setProbingShopIds((prev) => new Set([...prev, shopId]));
      probeShopMutation.mutate(
        { params: { path: { shopId } } },
        { onSettled: () => void refetch() },
      );
    },
    [catalogByShopId, probeShopMutation, refetch],
  );

  // ── URL-derived filter/sort/search state ──────────────────────────────────
  const search = searchParams.get("q") ?? "";
  const sortKey = (searchParams.get("sort") ?? SORT_DEFAULT) as SortKey;
  const sortDir = (searchParams.get("dir") ?? DIR_DEFAULT) as SortDir;
  const typeFilter = (searchParams.get("type") ?? TYPE_DEFAULT) as TypeFilter;
  const statusFilter = parseStatusFilter(searchParams.get("status"));
  const statsFilter = (searchParams.get("stats") ??
    STATS_DEFAULT) as StatsFilter;
  const featureFilter = new Set<FeatureKey>(
    (searchParams.get("features")?.split(",").filter(Boolean) ??
      []) as FeatureKey[],
  );
  const activityFilter = (searchParams.get("activity") ??
    ACTIVITY_DEFAULT) as ActivityFilter;
  // Legacy drawer links (?shop=<id>) now open the shop page.
  const legacyShopId = searchParams.get("shop");

  /** Update a single param, removing it when it equals the default. */
  function setParam(key: string, value: string | null, defaultValue?: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!value || value === defaultValue) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  }

  function setSearch(v: string) {
    setParam("q", v || null);
  }
  function setTypeFilter(v: TypeFilter) {
    setParam("type", v, TYPE_DEFAULT);
  }
  function setStatusFilter(v: Set<ClientStatus>) {
    setParam("status", serializeStatusFilter(v) || null);
  }
  function setStatsFilter(v: StatsFilter) {
    setParam("stats", v, STATS_DEFAULT);
  }
  function setFeatureFilter(v: Set<FeatureKey>) {
    setParam("features", [...v].join(",") || null);
  }
  function setActivityFilter(v: ActivityFilter) {
    setParam("activity", v, ACTIVITY_DEFAULT);
  }

  // ── Ephemeral state (not in URL) ──────────────────────────────────────────
  const [lastRefresh, setLastRefresh] = useState<{
    refreshedCount: number;
    failedCount: number;
  } | null>(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkTest, setBulkTest] = useState<boolean | undefined>(undefined);
  const [bulkStats, setBulkStats] = useState<boolean | undefined>(undefined);
  // undefined = unchanged, null = detach from their client (BBS-406).
  const [bulkClientId, setBulkClientId] = useState<string | null | undefined>(
    undefined,
  );
  const bulkMutation = useUpdateSuperAdminShopMeta();
  const bulkClientMutation = useSetSuperAdminShopsClient();
  const { data: clients } = useSuperAdminClients();

  const shops = useMemo(
    () =>
      (data?.shops ?? []).map((shop) => {
        const cat = catalogByShopId.get(shop.id);
        if (!cat) return shop;
        return {
          ...shop,
          catalogProductCount: cat.catalogProductCount ?? null,
          catalogProductCountProbedAt: cat.catalogProductCountProbedAt ?? null,
          isCatalogProductCountApproximate:
            cat.isCatalogProductCountApproximate ?? null,
          catalogArchivedProductCount: cat.catalogArchivedProductCount ?? null,
          isCatalogArchivedProductCountApproximate:
            cat.isCatalogArchivedProductCountApproximate ?? null,
          loadedCatalogProductCount: cat.loadedCatalogProductCount ?? null,
          loadedCatalogArchivedProductCount:
            cat.loadedCatalogArchivedProductCount ?? null,
        };
      }),
    [data?.shops, catalogByShopId],
  );
  const computedAt: number | null | undefined = data?.computedAt;
  const staleShopsCount = data?.staleShopsCount ?? 0;

  // Poll refetch while a probe batch is in-flight. Stop when set empties or 5min timeout.
  useEffect(() => {
    if (probingShopIds.size === 0) return;
    const interval = window.setInterval(() => {
      void refetch();
    }, 3000);
    const timeout = window.setTimeout(
      () => setProbingShopIds(new Set()),
      5 * 60_000,
    );
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [probingShopIds.size, refetch]);

  // After each refetch, drop shops whose catalogProductCountProbedAt moved past the snapshot.
  useEffect(() => {
    if (probingShopIds.size === 0 || shops.length === 0) return;
    const updated: string[] = [];
    for (const id of probingShopIds) {
      const shop = shops.find((s) => s.id === id) as
        | { id: string; catalogProductCountProbedAt?: number | null }
        | undefined;
      if (!shop) continue;
      const snap = probeSnapshotRef.current.get(id) ?? null;
      const current = shop.catalogProductCountProbedAt ?? null;
      if (current !== snap) updated.push(id);
    }
    if (updated.length > 0) {
      setProbingShopIds((prev) => {
        const next = new Set(prev);
        updated.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, [shops, probingShopIds]);

  // Shops matching the structural filters (type + status + stats + features) — drives KPIs
  const kpiShops = useMemo(() => {
    return shops.filter((sh) => {
      if (typeFilter === "prod" && sh.isTestShop) return false;
      if (typeFilter === "test" && !sh.isTestShop) return false;
      if (
        statusFilter.size > 0 &&
        !statusFilter.has(sh.clientStatus as ClientStatus)
      )
        return false;
      if (statsFilter === "in" && sh.isIncludedInStats === false) return false;
      if (statsFilter === "out" && sh.isIncludedInStats !== false) return false;
      if (featureFilter.size > 0) {
        // AND logic: shop must have every selected feature/integration
        const typedSh = sh as typeof sh & {
          hasExchanges?: boolean;
          hasGiftCode?: boolean;
          hasWorkflowLogistician?: boolean;
          activeIntegrations?: string[];
        };
        const shopFeatures = new Set<string>([
          ...(typedSh.hasExchanges ? ["exchanges"] : []),
          ...(typedSh.hasGiftCode ? ["giftCode"] : []),
          ...(typedSh.hasWorkflowLogistician ? ["logistician"] : []),
          ...(typedSh.activeIntegrations ?? []),
        ]);
        if (![...featureFilter].every((f) => shopFeatures.has(f))) return false;
      }
      if (activityFilter === "active" && sh.isActive === false) return false;
      if (activityFilter === "inactive" && sh.isActive !== false) return false;
      return true;
    });
  }, [
    shops,
    typeFilter,
    statusFilter,
    statsFilter,
    featureFilter,
    activityFilter,
  ]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return kpiShops
      .filter((sh) => {
        if (
          !sh.name.toLowerCase().includes(q) &&
          !sh.slug.toLowerCase().includes(q) &&
          !(sh.email ?? "").toLowerCase().includes(q)
        )
          return false;
        return true;
      })
      .sort((a, b) => {
        const va = a[sortKey] ?? 0;
        const vb = b[sortKey] ?? 0;
        if (typeof va === "string" && typeof vb === "string") {
          return sortDir === "asc"
            ? va.localeCompare(vb)
            : vb.localeCompare(va);
        }
        return sortDir === "asc"
          ? (va as number) - (vb as number)
          : (vb as number) - (va as number);
      });
  }, [kpiShops, search, sortKey, sortDir]);

  const totalOrders = filtered.reduce((s, sh) => s + sh.totalOrders, 0);
  const totalOrders30d = filtered.reduce((s, sh) => s + sh.ordersLast30Days, 0);
  const totalOrders7d = filtered.reduce((s, sh) => s + sh.ordersLast7Days, 0);
  const totalOrders24h = filtered.reduce(
    (s, sh) => s + sh.ordersLast24Hours,
    0,
  );
  const totalReturns = filtered.reduce(
    (s, sh) => s + sh.totalReturnRequests,
    0,
  );
  const totalReturns30d = filtered.reduce(
    (s, sh) => s + sh.returnRequestsLast30Days,
    0,
  );
  const totalReturns7d = filtered.reduce(
    (s, sh) => s + sh.returnRequestsLast7Days,
    0,
  );
  const totalReturns24h = filtered.reduce(
    (s, sh) => s + sh.returnRequestsLast24Hours,
    0,
  );
  const activeShops30d = filtered.filter(
    (sh) => sh.returnRequestsLast30Days > 0,
  ).length;
  const activeShops = filtered.filter(
    (sh) => sh.returnRequestsLast7Days > 0,
  ).length;

  const shopsWithExchanges = filtered.filter((sh) => sh.hasExchanges).length;
  const shopsWithGiftCode = filtered.filter((sh) => sh.hasGiftCode).length;
  const shopsWithWorkflowLogistician = filtered.filter(
    (sh) => sh.hasWorkflowLogistician,
  ).length;
  const total = filtered.length || 1; // avoid division by zero

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setParam("dir", sortDir === "asc" ? "desc" : "asc", DIR_DEFAULT);
    } else {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("sort", key);
          next.delete("dir"); // reset to default desc
          return next;
        },
        { replace: true },
      );
    }
  }

  function SortButton({ label, col }: { label: string; col: SortKey }) {
    return (
      <button
        onClick={() => toggleSort(col)}
        className="flex items-center gap-1 hover:text-foreground transition-colors"
      >
        {label}
        <ArrowUpDown
          className={`h-3 w-3 ${sortKey === col ? "text-violet-500" : "opacity-40"}`}
        />
      </button>
    );
  }

  const INCREMENTAL_MAX_AGE_MS = 15 * 60 * 1000; // 15 min

  async function handleRefresh(force = false) {
    const result = await refreshMutation.mutateAsync({
      params: {
        query: force ? {} : { maxAgeMs: String(INCREMENTAL_MAX_AGE_MS) },
      },
    });
    setLastRefresh(
      result
        ? {
            refreshedCount: result.refreshedCount ?? 0,
            failedCount: result.failedCount ?? 0,
          }
        : null,
    );
    await refetch();
  }

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const openShop = useCallback(
    (shopId: string) => navigate(`/admin/shops/${shopId}`),
    [navigate],
  );

  function toggleSelectAll() {
    if (filtered.every((sh) => selectedIds.has(sh.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((sh) => sh.id)));
    }
  }

  async function handleBulkApply() {
    const body: {
      isTestShop?: boolean | null;
      isIncludedInStats?: boolean | null;
    } = {};
    if (bulkTest !== undefined) body.isTestShop = bulkTest || null;
    if (bulkStats !== undefined)
      body.isIncludedInStats = bulkStats ? null : false;

    const ids = Array.from(selectedIds);
    const failedIds = new Set<string>();

    // allSettled: one refused shop must not hide which ones went through.
    if (Object.keys(body).length > 0) {
      const results = await Promise.allSettled(
        ids.map((id) =>
          bulkMutation.mutateAsync({ params: { path: { shopId: id } }, body }),
        ),
      );
      results.forEach(({ status }, index) => {
        if (status === "rejected") failedIds.add(ids[index]);
      });
    }

    // The client in one request per 100 shops (the route's limit), each a
    // transaction: a batch goes through whole or not at all.
    if (bulkClientId !== undefined) {
      for (let start = 0; start < ids.length; start += 100) {
        const batch = ids.slice(start, start + 100);
        try {
          await bulkClientMutation.mutateAsync({
            body: { shopIds: batch, clientAccountId: bulkClientId },
          });
        } catch {
          batch.forEach((id) => failedIds.add(id));
        }
      }
    }

    if (failedIds.size > 0) {
      window.alert(
        `${failedIds.size} boutique(s) sur ${ids.length} n'ont pas pu être modifiées. Elles restent sélectionnées.`,
      );
      setSelectedIds(failedIds);
      await refetch();
      return;
    }

    setSelectedIds(new Set());
    setBulkTest(undefined);
    setBulkStats(undefined);
    setBulkClientId(undefined);
    // The client route already refreshes the overview.
    if (Object.keys(body).length > 0) await refetch();
  }

  if (legacyShopId) {
    return <Navigate to={`/admin/shops/${legacyShopId}`} replace />;
  }

  return (
    <AdminPageShell
      title="Boutiques"
      mainClassName={selectedIds.size > 0 ? "pb-28" : undefined}
      actions={
        <div className="flex flex-wrap items-center justify-end gap-3">
          {computedAt && (
            <span
              className="flex items-center gap-1.5 text-xs text-muted-foreground"
              title="Comptes recalculés chaque heure ; pays, transporteurs et catalogue chaque nuit"
            >
              <Clock className="h-3 w-3" />
              Données calculées {formatRelative(computedAt)}
            </span>
          )}
          {staleShopsCount > 0 && (
            <span
              className="text-xs font-medium text-amber-600"
              title="Boutiques sans stats ou dont les stats ont plus de 2 h"
            >
              {staleShopsCount} boutique{staleShopsCount > 1 ? "s" : ""} en
              retard
            </span>
          )}
          {lastRefresh !== null && !refreshMutation.isPending && (
            <span className="text-xs text-muted-foreground">
              {lastRefresh.refreshedCount === 0 && lastRefresh.failedCount === 0
                ? "Tout à jour"
                : `${lastRefresh.refreshedCount} shop${lastRefresh.refreshedCount > 1 ? "s" : ""} recalculé${lastRefresh.refreshedCount > 1 ? "s" : ""}`}
              {lastRefresh.failedCount > 0 && (
                <span className="text-destructive">
                  {` · ${lastRefresh.failedCount} en échec`}
                </span>
              )}
            </span>
          )}
          <button
            onClick={() => syncCancelledMutation.mutate({})}
            disabled={syncCancelledMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border hover:bg-muted transition-colors disabled:opacity-50"
            title="Backfill isCancelled dans statisticsReturnRequestFlattened pour tous les shops (idempotent)"
          >
            <RotateCcw
              className={`h-3 w-3 ${syncCancelledMutation.isPending ? "animate-spin" : ""}`}
            />
            {syncCancelledMutation.isPending
              ? "Sync…"
              : syncCancelledMutation.isSuccess
                ? `✓ ${(syncCancelledMutation.data as { updated: number } | undefined)?.updated ?? 0} màj`
                : "Sync annulations"}
          </button>
          <button
            onClick={() =>
              probeCatalogMutation.mutate(
                {},
                {
                  onSuccess: () => {
                    const snap = new Map<string, number | null>();
                    for (const s of shops) {
                      const probedAt = (
                        s as { catalogProductCountProbedAt?: number | null }
                      ).catalogProductCountProbedAt;
                      snap.set(s.id, probedAt ?? null);
                    }
                    probeSnapshotRef.current = snap;
                    probeStartedAtRef.current = Date.now();
                    const ids = new Set(shops.map((s) => s.id));
                    probeInitialCountRef.current = ids.size;
                    setProbingShopIds(ids);
                  },
                },
              )
            }
            disabled={probeCatalogMutation.isPending || probingShopIds.size > 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border hover:bg-muted transition-colors disabled:opacity-50"
            title="Lance un probe productCount sur tous les shops (1 appel GQL par shop, tâche de fond)"
          >
            <Package
              className={`h-3 w-3 ${probeCatalogMutation.isPending || probingShopIds.size > 0 ? "animate-spin" : ""}`}
            />
            {probingShopIds.size > 0
              ? `Probe ${probeInitialCountRef.current - probingShopIds.size}/${probeInitialCountRef.current}`
              : probeCatalogMutation.isPending
                ? "Envoi…"
                : "Probe catalogs"}
          </button>
          <div className="flex items-center rounded-md border overflow-hidden">
            <button
              onClick={() => handleRefresh(false)}
              disabled={refreshMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50"
              title="Recalcule uniquement les shops dont les stats ont plus de 15 min"
            >
              <RefreshCw
                className={`h-3 w-3 ${refreshMutation.isPending ? "animate-spin" : ""}`}
              />
              {refreshMutation.isPending ? "Calcul…" : "Rafraîchir"}
            </button>
            <div className="w-px h-5 bg-border" />
            <button
              onClick={() => handleRefresh(true)}
              disabled={refreshMutation.isPending}
              className="px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
              title="Recalcule tous les shops sans exception"
            >
              Tout
            </button>
          </div>
        </div>
      }
    >
      {/* Filters + Search */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Search — first */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Rechercher un shop…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64 rounded-md border bg-background pl-9 pr-3 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        <div className="w-px h-5 bg-border" />

        {/* Type filter — single-select dropdown */}
        <FilterDropdown
          label={
            typeFilter === "prod"
              ? "Prod"
              : typeFilter === "test"
                ? "Test"
                : "Type : Tous"
          }
        >
          {(["prod", "test", "all"] as TypeFilter[]).map((v) => (
            <DropdownRadioItem
              key={v}
              checked={typeFilter === v}
              onChange={() => setTypeFilter(v)}
            >
              {v === "prod" ? (
                "Production"
              ) : v === "test" ? (
                <span className="flex items-center gap-1.5">
                  <FlaskConical className="h-3 w-3" /> Test
                </span>
              ) : (
                "Tous"
              )}
            </DropdownRadioItem>
          ))}
        </FilterDropdown>

        {/* Status filter — multi-select dropdown */}
        <FilterDropdown
          label="Statut"
          activeCount={statusFilter.size || undefined}
          onClear={() => setStatusFilter(new Set())}
        >
          {(
            ["prospect", "onboarding", "active", "paused", "churned"] as const
          ).map((s) => (
            <DropdownCheckItem
              key={s}
              checked={statusFilter.has(s)}
              onChange={() => {
                const next = new Set(statusFilter);
                if (next.has(s)) next.delete(s);
                else next.add(s);
                setStatusFilter(next);
              }}
            >
              <span
                className={`rounded-full px-2 py-px text-[11px] font-medium ${CLIENT_STATUS_LABELS[s].color}`}
              >
                {CLIENT_STATUS_LABELS[s].label}
              </span>
            </DropdownCheckItem>
          ))}
        </FilterDropdown>

        {/* Stats filter — single-select dropdown */}
        <FilterDropdown
          label={
            statsFilter === "in"
              ? "Stats : Inclus"
              : statsFilter === "out"
                ? "Stats : Exclus"
                : "Stats"
          }
        >
          {(["all", "in", "out"] as StatsFilter[]).map((v) => (
            <DropdownRadioItem
              key={v}
              checked={statsFilter === v}
              onChange={() => setStatsFilter(v)}
            >
              {v === "all" ? "Tous" : v === "in" ? "Inclus" : "Exclus"}
            </DropdownRadioItem>
          ))}
        </FilterDropdown>

        {/* Activity filter — single-select dropdown */}
        <FilterDropdown
          label={
            activityFilter === "active"
              ? "Actifs"
              : activityFilter === "inactive"
                ? "Inactifs"
                : "Activité"
          }
        >
          {(["all", "active", "inactive"] as ActivityFilter[]).map((v) => (
            <DropdownRadioItem
              key={v}
              checked={activityFilter === v}
              onChange={() => setActivityFilter(v)}
            >
              {v === "all" ? "Tous" : v === "active" ? "Actifs" : "Inactifs"}
            </DropdownRadioItem>
          ))}
        </FilterDropdown>

        {/* Features & integrations — multi-select dropdown */}
        <FilterDropdown
          label="Features"
          activeCount={featureFilter.size || undefined}
          onClear={() => setFeatureFilter(new Set())}
        >
          {/* Core features */}
          <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Fonctionnalités
          </div>
          {(
            [
              {
                key: "exchanges" as FeatureKey,
                label: "Échanges",
                icon: <ArrowLeftRight className="h-3 w-3 text-blue-500" />,
              },
              {
                key: "giftCode" as FeatureKey,
                label: "Avoirs",
                icon: <Gift className="h-3 w-3 text-violet-500" />,
              },
              {
                key: "logistician" as FeatureKey,
                label: "Workflow logisticien",
                icon: <Package className="h-3 w-3 text-orange-500" />,
              },
            ] as const
          ).map(({ key, label, icon }) => (
            <DropdownCheckItem
              key={key}
              checked={featureFilter.has(key)}
              onChange={() => {
                const next = new Set(featureFilter);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                setFeatureFilter(next);
              }}
            >
              <span className="flex items-center gap-1.5">
                {icon}
                {label}
              </span>
            </DropdownCheckItem>
          ))}
          {/* Integrations */}
          <div className="mx-3 my-1 h-px bg-border" />
          <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Intégrations
          </div>
          {Object.entries(INTEGRATIONS).map(([key, { label, pillColor }]) => (
            <DropdownCheckItem
              key={key}
              checked={featureFilter.has(key as FeatureKey)}
              onChange={() => {
                const next = new Set(featureFilter);
                const k = key as FeatureKey;
                if (next.has(k)) next.delete(k);
                else next.add(k);
                setFeatureFilter(next);
              }}
            >
              <span className="flex items-center gap-1.5">
                <span
                  className={`inline-block w-2 h-2 rounded-full shrink-0 ${pillColor}`}
                />
                {label}
              </span>
            </DropdownCheckItem>
          ))}
        </FilterDropdown>
      </div>

      {/* KPI section */}
      <div className="flex gap-3 items-stretch">
        {/* Shops block */}
        <div className="flex flex-col gap-3 w-36 shrink-0">
          <KpiCard
            label="Shops"
            value={filtered.length}
            icon={<Package className="h-4 w-4 text-muted-foreground" />}
          />
          <KpiCard
            label="Actifs 30j"
            value={activeShops30d}
            icon={<TrendingUp className="h-4 w-4 text-amber-500" />}
            highlight="amber"
          />
          <KpiCard
            label="Actifs 7j"
            value={activeShops}
            icon={<TrendingUp className="h-4 w-4 text-emerald-500" />}
            highlight="emerald"
          />
        </div>

        {/* Period stat panels */}
        <div className="flex-1 grid grid-cols-4 gap-3">
          <StatPanel
            period="Total"
            orders={totalOrders}
            returns={totalReturns}
          />
          <StatPanel
            period="30 jours"
            orders={totalOrders30d}
            returns={totalReturns30d}
            color="amber"
          />
          <StatPanel
            period="7 jours"
            orders={totalOrders7d}
            returns={totalReturns7d}
            color="emerald"
          />
          <StatPanel
            period="24h"
            orders={totalOrders24h}
            returns={totalReturns24h}
            color="blue"
          />
        </div>
      </div>

      {/* Feature KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <FeatureKpi
          icon={<ArrowLeftRight className="h-4 w-4 text-blue-500" />}
          label="Échanges activés"
          count={shopsWithExchanges}
          total={filtered.length}
          pct={Math.round((shopsWithExchanges / total) * 100)}
          color="blue"
        />
        <FeatureKpi
          icon={<Gift className="h-4 w-4 text-violet-500" />}
          label="Avoirs activés"
          count={shopsWithGiftCode}
          total={filtered.length}
          pct={Math.round((shopsWithGiftCode / total) * 100)}
          color="violet"
        />
        <FeatureKpi
          icon={<Package className="h-4 w-4 text-orange-500" />}
          label="Workflow logisticien"
          count={shopsWithWorkflowLogistician}
          total={filtered.length}
          pct={Math.round((shopsWithWorkflowLogistician / total) * 100)}
          color="orange"
        />
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-background overflow-hidden overflow-x-auto">
        {isPending ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
            Chargement…
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center py-20 text-destructive text-sm">
            Erreur lors du chargement des données
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
            Aucun shop trouvé
          </div>
        ) : (
          <table className="w-full min-w-[1400px] text-sm">
            <thead className="bg-muted/50">
              {/* Period group headers */}
              <tr className="border-b border-border/50">
                <th className="px-4 py-1.5 text-left" colSpan={3} />
                <th className="hidden lg:table-cell" />
                <th className="hidden lg:table-cell" />
                <th
                  className="px-3 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hidden xl:table-cell border-l border-border/50"
                  colSpan={2}
                >
                  Total
                </th>
                <th
                  className="px-3 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wider text-amber-600 hidden lg:table-cell border-l border-amber-200 bg-amber-50/60"
                  colSpan={2}
                >
                  30j
                </th>
                <th
                  className="px-3 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wider text-emerald-600 hidden md:table-cell border-l border-emerald-200 bg-emerald-50/60"
                  colSpan={2}
                >
                  7j
                </th>
                <th
                  className="px-3 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wider text-blue-500 border-l border-blue-200"
                  colSpan={2}
                >
                  24h
                </th>
                <th className="hidden lg:table-cell" colSpan={2} />
              </tr>
              {/* Column headers */}
              <tr className="border-b">
                <th className="w-10 px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={
                      filtered.length > 0 &&
                      filtered.every((sh) => selectedIds.has(sh.id))
                    }
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          selectedIds.size > 0 &&
                          !filtered.every((sh) => selectedIds.has(sh.id));
                    }}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 cursor-pointer accent-violet-600"
                  />
                </th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                  <SortButton label="Shop" col="name" />
                </th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground hidden md:table-cell">
                  Plan
                </th>
                <th className="px-3 py-2 text-right font-medium text-muted-foreground hidden lg:table-cell">
                  <SortButton label="Catalog" col="catalogProductCount" />
                </th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground hidden lg:table-cell">
                  Features
                </th>
                {/* Total */}
                <th className="px-3 py-2 text-right font-medium text-muted-foreground hidden xl:table-cell border-l border-border/50">
                  <SortButton label="Ret" col="totalReturnRequests" />
                </th>
                <th className="px-3 py-2 text-right font-medium text-muted-foreground hidden xl:table-cell">
                  <SortButton label="Cmd" col="totalOrders" />
                </th>
                {/* 30j */}
                <th className="px-3 py-2 text-right font-medium text-amber-600 hidden lg:table-cell border-l border-amber-200 bg-amber-50/60">
                  <SortButton label="Ret" col="returnRequestsLast30Days" />
                </th>
                <th className="px-3 py-2 text-right font-medium text-amber-600 hidden lg:table-cell bg-amber-50/60">
                  <SortButton label="Cmd" col="ordersLast30Days" />
                </th>
                {/* 7j */}
                <th className="px-3 py-2 text-right font-medium text-emerald-600 hidden md:table-cell border-l border-emerald-200 bg-emerald-50/60">
                  <SortButton label="Ret" col="returnRequestsLast7Days" />
                </th>
                <th className="px-3 py-2 text-right font-medium text-emerald-600 hidden md:table-cell bg-emerald-50/60">
                  <SortButton label="Cmd" col="ordersLast7Days" />
                </th>
                {/* 24h */}
                <th className="px-3 py-2 text-right font-medium text-blue-500 border-l border-blue-200">
                  <SortButton label="Ret" col="returnRequestsLast24Hours" />
                </th>
                <th className="px-3 py-2 text-right font-medium text-blue-500">
                  <SortButton label="Cmd" col="ordersLast24Hours" />
                </th>
                <th className="px-3 py-2 text-center font-medium text-muted-foreground hidden lg:table-cell">
                  Activité
                </th>
                <th className="px-3 py-2 text-right font-medium text-muted-foreground hidden xl:table-cell">
                  <SortButton label="Dernière activité" col="lastActivityAt" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((shop) => (
                <ShopRow
                  key={shop.id}
                  shop={shop as ShopRowData}
                  isSelected={selectedIds.has(shop.id)}
                  isProbingCatalog={probingShopIds.has(shop.id)}
                  isCatalogPending={isCatalogPending}
                  onToggle={toggleSelect}
                  onOpen={openShop}
                  onProbeCount={handleProbeShopCount}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-muted-foreground text-center">
        {filtered.length} shop{filtered.length > 1 ? "s" : ""} · Cliquer pour
        ouvrir la boutique
      </p>
      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <BulkActionBar
          count={selectedIds.size}
          bulkTest={bulkTest}
          onBulkTest={setBulkTest}
          bulkStats={bulkStats}
          onBulkStats={setBulkStats}
          bulkClientId={bulkClientId}
          onBulkClientId={setBulkClientId}
          clients={clients ?? []}
          isPending={bulkMutation.isPending || bulkClientMutation.isPending}
          onApply={handleBulkApply}
          onClear={() => {
            setSelectedIds(new Set());
            setBulkTest(undefined);
            setBulkStats(undefined);
            setBulkClientId(undefined);
          }}
        />
      )}
    </AdminPageShell>
  );
}

// ─── Shop Row (memoized — only re-renders when isSelected changes) ────────────

const ShopRow = memo(function ShopRow({
  shop,
  isSelected,
  isProbingCatalog,
  isCatalogPending,
  onToggle,
  onOpen,
  onProbeCount,
}: {
  shop: ShopRowData;
  isSelected: boolean;
  isProbingCatalog: boolean;
  isCatalogPending: boolean;
  onToggle: (id: string) => void;
  onOpen: (shopId: string) => void;
  onProbeCount: (shopId: string) => void;
}) {
  return (
    <tr
      onClick={() => onOpen(shop.id)}
      className={[
        "cursor-pointer hover:bg-muted/40 transition-colors",
        isSelected ? "bg-violet-50" : "",
      ].join(" ")}
    >
      <td
        className="w-10 px-3 py-3 text-center"
        onClick={(e) => {
          e.stopPropagation();
          onToggle(shop.id);
        }}
      >
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          className="h-4 w-4 cursor-pointer accent-violet-600 pointer-events-none"
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-medium">{shop.name}</span>
          {shop.isActive === false && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[11px] font-medium text-red-600">
              <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
              inactif
            </span>
          )}
          {shop.isTestShop && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
              <FlaskConical className="h-2.5 w-2.5" />
              test
            </span>
          )}
          {shop.clientStatus && (
            <span
              className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium ${CLIENT_STATUS_LABELS[shop.clientStatus as ClientStatus]?.color ?? "bg-gray-100 text-gray-500"}`}
            >
              {CLIENT_STATUS_LABELS[shop.clientStatus as ClientStatus]?.label ??
                shop.clientStatus}
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {shop.slug}
          {shop.clientName && (
            <span className="ml-1.5 text-violet-700">· {shop.clientName}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 hidden md:table-cell">
        {shop.planCode ? (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-mono">
            {shop.planCode}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      {/* Catalog product count */}
      <td className="px-3 py-3 text-right hidden lg:table-cell tabular-nums">
        <div className="inline-flex items-center justify-end gap-1 group/catalog">
          {isProbingCatalog || isCatalogPending ? (
            <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />
          ) : shop.catalogProductCount !== null &&
            shop.catalogProductCount !== undefined ? (
            <div
              title={
                shop.catalogProductCountProbedAt
                  ? `dernier sync: ${formatRelative(shop.catalogProductCountProbedAt)}`
                  : undefined
              }
              className="text-xs font-medium whitespace-nowrap flex flex-col items-end gap-0.5"
            >
              <span>
                {shop.loadedCatalogProductCount !== null &&
                shop.loadedCatalogProductCount !== undefined
                  ? `${new Intl.NumberFormat("fr-FR").format(shop.loadedCatalogProductCount)} / `
                  : ""}
                {shop.isCatalogProductCountApproximate
                  ? `+ de ${new Intl.NumberFormat("fr-FR", { notation: "compact" }).format(shop.catalogProductCount)}`
                  : new Intl.NumberFormat("fr-FR").format(
                      shop.catalogProductCount,
                    )}
              </span>
              {shop.catalogArchivedProductCount !== null &&
              shop.catalogArchivedProductCount !== undefined ? (
                <span className="text-muted-foreground font-normal">
                  {shop.loadedCatalogArchivedProductCount !== null &&
                  shop.loadedCatalogArchivedProductCount !== undefined
                    ? `${new Intl.NumberFormat("fr-FR").format(shop.loadedCatalogArchivedProductCount)} / `
                    : ""}
                  {shop.isCatalogArchivedProductCountApproximate
                    ? `+ de ${new Intl.NumberFormat("fr-FR", { notation: "compact" }).format(shop.catalogArchivedProductCount)}`
                    : new Intl.NumberFormat("fr-FR").format(
                        shop.catalogArchivedProductCount,
                      )}{" "}
                  arch.
                </span>
              ) : null}
            </div>
          ) : (
            <span className="text-muted-foreground text-xs">—</span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onProbeCount(shop.id);
            }}
            disabled={isProbingCatalog}
            className="opacity-0 group-hover/catalog:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted disabled:opacity-0"
            title="Relancer le probe catalog pour ce shop"
          >
            <RefreshCw className="h-3 w-3 text-muted-foreground" />
          </button>
        </div>
      </td>
      {/* Feature flags */}
      <td className="px-3 py-3 hidden lg:table-cell">
        <div className="flex items-center gap-1 flex-wrap">
          <span
            title="Échanges"
            className={[
              "inline-flex items-center rounded px-1 py-0.5 text-[11px] font-medium",
              shop.hasExchanges
                ? "bg-blue-100 text-blue-700"
                : "bg-muted text-muted-foreground",
            ].join(" ")}
          >
            <ArrowLeftRight className="h-2.5 w-2.5" />
          </span>
          <span
            title="Avoirs / Bons d'achat"
            className={[
              "inline-flex items-center rounded px-1 py-0.5 text-[11px] font-medium",
              shop.hasGiftCode
                ? "bg-violet-100 text-violet-700"
                : "bg-muted text-muted-foreground",
            ].join(" ")}
          >
            <Gift className="h-2.5 w-2.5" />
          </span>
          <span
            title="Workflow logisticien"
            className={[
              "inline-flex items-center rounded px-1 py-0.5 text-[11px] font-medium",
              shop.hasWorkflowLogistician
                ? "bg-orange-100 text-orange-700"
                : "bg-muted text-muted-foreground",
            ].join(" ")}
          >
            <Package className="h-2.5 w-2.5" />
          </span>
          {shop.activeIntegrations.map((k) => {
            const info = INTEGRATIONS[k];
            return (
              <span
                key={k}
                title={info?.label ?? k}
                className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${info?.pillColor ?? "bg-muted-foreground"}`}
              />
            );
          })}
        </div>
      </td>
      {/* Total */}
      <td className="px-3 py-3 text-right tabular-nums hidden xl:table-cell text-muted-foreground border-l border-border/50">
        {shop.totalReturnRequests.toLocaleString("fr-FR")}
      </td>
      <td className="px-3 py-3 text-right tabular-nums hidden xl:table-cell text-muted-foreground">
        {shop.totalOrders.toLocaleString("fr-FR")}
      </td>
      {/* 30j */}
      <td className="px-3 py-3 text-right tabular-nums hidden lg:table-cell text-amber-700 border-l border-amber-200 bg-amber-50/20">
        {shop.returnRequestsLast30Days.toLocaleString("fr-FR")}
      </td>
      <td className="px-3 py-3 text-right tabular-nums hidden lg:table-cell text-amber-700 bg-amber-50/20">
        {shop.ordersLast30Days.toLocaleString("fr-FR")}
      </td>
      {/* 7j */}
      <td className="px-3 py-3 text-right tabular-nums hidden md:table-cell text-emerald-700 border-l border-emerald-200 bg-emerald-50/20">
        {shop.returnRequestsLast7Days.toLocaleString("fr-FR")}
      </td>
      <td className="px-3 py-3 text-right tabular-nums hidden md:table-cell text-emerald-700 bg-emerald-50/20">
        {shop.ordersLast7Days.toLocaleString("fr-FR")}
      </td>
      {/* 24h */}
      <td className="px-3 py-3 text-right tabular-nums font-medium text-blue-700 border-l border-blue-200">
        {shop.returnRequestsLast24Hours.toLocaleString("fr-FR")}
      </td>
      <td className="px-3 py-3 text-right tabular-nums font-medium text-blue-700">
        {shop.ordersLast24Hours.toLocaleString("fr-FR")}
      </td>
      <td className="px-3 py-3 text-center hidden lg:table-cell">
        <ActivityBadge last7Days={shop.returnRequestsLast7Days} />
      </td>
      <td className="px-3 py-3 text-right text-muted-foreground hidden xl:table-cell">
        {formatDate(shop.lastActivityAt)}
      </td>
    </tr>
  );
});

// ─── Bulk Action Bar ──────────────────────────────────────────────────────────

function TriPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
        active
          ? "bg-violet-600 text-white"
          : "bg-muted text-muted-foreground hover:bg-muted/80",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

function BulkActionBar({
  count,
  bulkTest,
  onBulkTest,
  bulkStats,
  onBulkStats,
  bulkClientId,
  onBulkClientId,
  clients,
  isPending,
  onApply,
  onClear,
}: {
  count: number;
  bulkTest: boolean | undefined;
  onBulkTest: (v: boolean | undefined) => void;
  bulkStats: boolean | undefined;
  onBulkStats: (v: boolean | undefined) => void;
  bulkClientId: string | null | undefined;
  onBulkClientId: (v: string | null | undefined) => void;
  clients: Array<{ id: string; name: string }>;
  isPending: boolean;
  onApply: () => void;
  onClear: () => void;
}) {
  const hasChanges =
    bulkTest !== undefined ||
    bulkStats !== undefined ||
    bulkClientId !== undefined;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 border-t bg-background shadow-[0_-4px_16px_rgba(0,0,0,0.08)]">
      <div className="mx-auto max-w-7xl px-6 py-3 flex items-center gap-5">
        {/* Count */}
        <span className="text-sm font-semibold tabular-nums shrink-0">
          {count} shop{count > 1 ? "s" : ""} sélectionné{count > 1 ? "s" : ""}
        </span>

        <div className="w-px h-5 bg-border shrink-0" />

        {/* Test */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Test</span>
          <TriPill
            label="—"
            active={bulkTest === undefined}
            onClick={() => onBulkTest(undefined)}
          />
          <TriPill
            label="Oui"
            active={bulkTest === true}
            onClick={() => onBulkTest(true)}
          />
          <TriPill
            label="Non"
            active={bulkTest === false}
            onClick={() => onBulkTest(false)}
          />
        </div>

        <div className="w-px h-5 bg-border shrink-0" />

        {/* Stats */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Stats</span>
          <TriPill
            label="—"
            active={bulkStats === undefined}
            onClick={() => onBulkStats(undefined)}
          />
          <TriPill
            label="Inclus"
            active={bulkStats === true}
            onClick={() => onBulkStats(true)}
          />
          <TriPill
            label="Exclus"
            active={bulkStats === false}
            onClick={() => onBulkStats(false)}
          />
        </div>

        <div className="w-px h-5 bg-border shrink-0" />

        {/* Client: the status lives on the client now (BBS-406). */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Client</span>
          <select
            value={
              bulkClientId === undefined
                ? "__unchanged__"
                : (bulkClientId ?? "__detach__")
            }
            onChange={(e) => {
              const v = e.target.value;
              onBulkClientId(
                v === "__unchanged__"
                  ? undefined
                  : v === "__detach__"
                    ? null
                    : v,
              );
            }}
            className="max-w-56 rounded-md border bg-background px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="__unchanged__">— Inchangé</option>
            <option value="__detach__">Détacher</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <button
            onClick={onClear}
            className="rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
          >
            Désélectionner
          </button>
          <button
            onClick={onApply}
            disabled={!hasChanges || isPending}
            className="rounded-md bg-violet-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-violet-700 transition-colors disabled:opacity-40"
          >
            {isPending ? "Enregistrement…" : "Appliquer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  icon,
  highlight,
  small,
}: {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  highlight?: "emerald" | "blue" | "amber";
  small?: boolean;
}) {
  const borderClass =
    highlight === "emerald"
      ? "border-emerald-200 border-l-4 border-l-emerald-400"
      : highlight === "blue"
        ? "border-blue-200 border-l-4 border-l-blue-500"
        : highlight === "amber"
          ? "border-amber-200 border-l-4 border-l-amber-400"
          : "";
  const textClass =
    highlight === "emerald"
      ? "text-emerald-700"
      : highlight === "blue"
        ? "text-blue-700"
        : highlight === "amber"
          ? "text-amber-700"
          : "";

  return (
    <div className={`rounded-lg border bg-background p-3 ${borderClass}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-muted-foreground">
          {label}
        </span>
        {icon}
      </div>
      <div
        className={`tabular-nums font-bold ${small ? "text-lg" : "text-2xl"} ${textClass}`}
      >
        {value}
      </div>
    </div>
  );
}
